import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { fmtDate, localToday as today } from "./equipmentMeta";

const inputCls = "px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand";

export const HistoryLog = ({ equipment, kind, title, canAdd, canDelete, onChange }) => {
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const entries = equipment[`${kind}_history`] || [];

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/equipment/${equipment.id}/history`, { kind, date, note: note.trim() });
      setNote("");
      setDate(today());
      onChange(r.data);
      toast.success("Entry added");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      const r = await api.delete(`/equipment/${equipment.id}/history/${kind}/${id}`);
      onChange(r.data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`${kind}-history`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand border-b border-zinc-200 pb-1 mb-2">
        {title} <span className="text-zinc-400">({entries.length})</span>
      </div>
      {canAdd && (
        <form onSubmit={add} className="flex gap-2 mb-3 flex-wrap">
          <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} data-testid={`${kind}-history-date`} className={inputCls} />
          <input required value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="What happened?" data-testid={`${kind}-history-note`} className={`${inputCls} flex-1 min-w-0 basis-40`} />
          <button type="submit" disabled={busy || !note.trim()} data-testid={`${kind}-history-add`}
            className="bg-zinc-900 text-white px-3 py-2 text-sm font-bold flex items-center gap-1 hover:bg-brand transition-colors disabled:opacity-50">
            <Plus size={14} /> Add
          </button>
        </form>
      )}
      {entries.length === 0 ? (
        <p className="text-sm text-zinc-400">No entries yet.</p>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {entries.map((h) => (
            <li key={h.id} className="py-2 flex items-start gap-3" data-testid={`${kind}-entry-${h.id}`}>
              <span className="text-xs font-bold text-zinc-900 w-24 shrink-0">{fmtDate(h.date)}</span>
              <span className="text-sm text-zinc-700 flex-1 min-w-0 break-words whitespace-pre-wrap">{h.note}
                {h.by_name && <span className="block text-[11px] text-zinc-400">by {h.by_name}</span>}
              </span>
              {canDelete && (
                <button onClick={() => remove(h.id)} data-testid={`${kind}-entry-delete-${h.id}`} className="text-zinc-300 hover:text-red-600"><Trash2 size={14} /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
